import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  AssignmentStatus,
  ComplianceHeatmapCell,
  ComplianceSnapshot,
  ComplianceSummary,
  Course,
  CourseEnrollment,
  Group,
  Login,
  Tag,
} from '../../apis/api-client';
import { coursesClient, groupsClient, reportsClient, tagsClient, usersClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import { useEffectiveWorkspaceId } from '../../hooks/useEffectiveWorkspaceId';
import { usePagedList } from '../../hooks/usePagedList';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import PagedTable, { PagedTableColumn } from '../common/PagedTable';
import UserAutocomplete from '../common/UserAutocomplete';
import { CourseAutocomplete, EmptyState, EntityLink, TagAutocomplete } from '../common';
import { ArrowBackIcon, DownloadIcon, FilterIcon, WorkspacesIcon } from '../icons';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '../ui';
import { statusColor, statusLabel } from '../../utils/reportingHelpers';
import ComplianceSummaryStrip from './ComplianceSummaryStrip';

type PivotMode = 'group' | 'person' | 'course' | 'tag';
type ViewMode = 'heatmap' | 'table';

// One drill step records what was *added* on this drill so Back can pop it.
// With URL-bound filters, popping means deleting those IDs from the URL params.
interface DrillStep {
  pivotBefore: PivotMode;
  viewModeBefore: ViewMode;
  added: {
    userIds: number[];
    courseIds: number[];
    groupIds: number[];
    includeUngrouped: boolean;
    tagIds: number[];
  };
}

// Pick worst status for a cell (Overdue > Active > Upcoming > Completed).
function cellWorstStatus(c: ComplianceHeatmapCell): number | null {
  if (c.overdue > 0) return AssignmentStatus.Overdue;
  if (c.active > 0) return AssignmentStatus.Active;
  if (c.upcoming > 0) return AssignmentStatus.Upcoming;
  if (c.completed > 0) return AssignmentStatus.Completed;
  return null;
}

function cellPercent(c: ComplianceHeatmapCell): string {
  const total = c.upcoming + c.active + c.overdue + c.completed;
  if (total === 0) return '—';
  return `${Math.round((c.completed / total) * 100)}%`;
}

function cellTotal(c: ComplianceHeatmapCell): number {
  return c.upcoming + c.active + c.overdue + c.completed;
}

interface ComplianceDashboardProps {
  /**
   * Owner mode (learner-facing "My Group" surface). When provided, the
   * dashboard scopes to the caller's owned groups: the group dropdown is
   * seeded from these instead of the admin-only group search, the admin-only
   * snapshot timeline is skipped, and the People/Tags filters are hidden.
   * The server independently hard-scopes a learner-owner's compliance queries
   * to their owned groups, so this prop is purely presentation.
   */
  ownedGroups?: Group[];
}

export default function ComplianceDashboard({ ownedGroups }: ComplianceDashboardProps = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const effectiveWorkspaceId = useEffectiveWorkspaceId();
  const location = useLocation();
  const ownerMode = ownedGroups !== undefined;

  // ─── URL-backed filters ────────────────────────────────────────────
  const { searchParams, getInt, getIntArray, getString, getBool, setParams } = useUrlFilters();
  const pivotBy = (getString('pivot', 'group') as PivotMode);
  const viewMode = (getString('view', 'table') as ViewMode);
  const filterStatus = getInt('status');
  const filterDueFrom = getString('dueFrom');
  const filterDueTo = getString('dueTo');
  const snapshotDays = getInt('snapDays') ?? 90;
  const userIds = getIntArray('userIds');
  const courseIdArr = getIntArray('courseIds');
  const groupIdArr = getIntArray('groupIds');
  const includeUngrouped = getBool('includeUngrouped', false);
  const tagIdArr = getIntArray('tagIds');
  const initialQuery = getString('q');

  // ─── Hydrated chip state (URL holds IDs; we keep object copies for chips) ─
  const [filterPeople, setFilterPeople] = useState<Login[]>([]);
  const [filterCourses, setFilterCourses] = useState<Course[]>([]);
  const [filterCourseTagIds, setFilterCourseTagIds] = useState<Tag[]>([]);

  // Groups bulk-load (capped at 200) for the dropdown + drill-down lookups.
  const [groups, setGroups] = useState<Group[]>([]);

  // ─── Server-derived state ─────────────────────────────────────────
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [heatmapCells, setHeatmapCells] = useState<ComplianceHeatmapCell[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [snapshots, setSnapshots] = useState<ComplianceSnapshot[]>([]);
  const [downloading, setDownloading] = useState(false);

  // ─── Drill stack: local (back-button within the heatmap) ──────────
  const [drillStack, setDrillStack] = useState<DrillStep[]>([]);

  const statusValue = filterStatus;
  const dueFromIso = filterDueFrom ? new Date(filterDueFrom).toISOString() : undefined;
  const dueToIso = filterDueTo ? new Date(filterDueTo).toISOString() : undefined;

  // Stable cache key for the data effects below.
  const filterKey = [
    effectiveWorkspaceId ?? null,
    userIds.join(','),
    courseIdArr.join(','),
    groupIdArr.join(','),
    includeUngrouped,
    tagIdArr.join(','),
    statusValue ?? '',
    dueFromIso ?? '',
    dueToIso ?? '',
  ].join('|');

  // Workspace switch: clear workspace-scoped filters (group / student / course),
  // and clear any in-progress heatmap drill stack so Back doesn't reference
  // gone-with-the-wind IDs. Tags + status + dates carry across.
  const prevWsIdRef = useRef(effectiveWorkspaceId);
  useEffect(() => {
    if (prevWsIdRef.current != null && prevWsIdRef.current !== effectiveWorkspaceId) {
      setDrillStack([]);
      setParams({ userIds: null, courseIds: null, groupIds: null, includeUngrouped: false });
    }
    prevWsIdRef.current = effectiveWorkspaceId;
  }, [effectiveWorkspaceId, setParams]);

  // ─── Group lookup (bulk, for drill-down) ──────────────────────────
  // In owner mode the caller is a learner who can't call the admin group
  // search — seed the dropdown from the groups they own instead.
  useEffect(() => {
    if (ownedGroups) { setGroups(ownedGroups); return; }
    if (!effectiveWorkspaceId) { setGroups([]); return; }
    (async () => {
      try {
        const grpResult = await groupsClient.searchGroups(effectiveWorkspaceId, undefined, 1, 200, undefined);
        setGroups(grpResult.items ?? []);
      } catch { /* non-fatal */ }
    })();
  }, [effectiveWorkspaceId, ownedGroups]);

  // ─── Chip hydration from URL IDs ──────────────────────────────────

  useEffect(() => {
    const want = new Set(courseIdArr);
    const have = new Set(filterCourses.map(c => c.id!).filter(Boolean));
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterCourses(courseIdArr.map(id => filterCourses.find(c => c.id === id) ?? ({ id, title: `Course #${id}` } as Course)));
    const missing = courseIdArr.filter(id => !filterCourses.some(c => c.id === id));
    missing.forEach(id => {
      coursesClient.getCourseById(id).then(c => {
        if (c) setFilterCourses(prev => prev.map(p => p.id === id ? c : p));
      }).catch(() => { /* placeholder is good enough */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIdArr.join(',')]);

  useEffect(() => {
    if (!effectiveWorkspaceId) return;
    const want = new Set(userIds);
    const have = new Set(filterPeople.map(p => p.id!).filter(Boolean));
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterPeople(userIds.map(id => filterPeople.find(p => p.id === id) ?? ({ id } as Login)));
    if (userIds.length > 0) {
      usersClient.searchUsersPaged(undefined, undefined, undefined, undefined, 1, userIds.length, userIds)
        .then(res => {
          const fetched = res.items ?? [];
          setFilterPeople(prev => prev.map(p => fetched.find(f => f.id === p.id) ?? p));
        }).catch(() => { /* placeholder is good enough */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(','), effectiveWorkspaceId]);

  useEffect(() => {
    const want = new Set(tagIdArr);
    const have = new Set(filterCourseTagIds.map(t => t.id!).filter(Boolean));
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterCourseTagIds(tagIdArr.map(id => filterCourseTagIds.find(t => t.id === id) ?? ({ id, name: `Tag #${id}` } as Tag)));
    if (tagIdArr.length > 0) {
      tagsClient.searchTags(undefined, undefined, undefined, false, false, tagIdArr, 1, tagIdArr.length)
        .then(res => {
          const fetched = res.items ?? [];
          setFilterCourseTagIds(prev => prev.map(p => fetched.find(t => t.id === p.id) ?? p));
        }).catch(() => { /* placeholder is good enough */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagIdArr.join(',')]);

  // Group selections derived from URL (real groups + optional "No Group" sentinel).
  const filterGroupSelections = useMemo<(Group | { id: 0; name: 'No Group' })[]>(() => {
    const fromIds = groupIdArr
      .map(id => groups.find(g => g.id === id))
      .filter((g): g is Group => !!g);
    return includeUngrouped
      ? [...fromIds, { id: 0 as const, name: 'No Group' as const }]
      : fromIds;
  }, [groupIdArr, includeUngrouped, groups]);

  // ─── Server fetches ────────────────────────────────────────────────

  useEffect(() => {
    if (!effectiveWorkspaceId) { setSummary(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const result = await reportsClient.complianceSummary(
          effectiveWorkspaceId,
          userIds.length > 0 ? userIds : undefined,
          courseIdArr.length > 0 ? courseIdArr : undefined,
          groupIdArr.length > 0 ? groupIdArr : undefined,
          includeUngrouped,
          tagIdArr.length > 0 ? tagIdArr : undefined, // course-tag filter — must match the heatmap/table so the summary numbers reflect the same scope
          undefined, // person-tag filter is a separate param, not exposed here
          statusValue,
          undefined, undefined,
          dueFromIso as any, dueToIso as any,
          undefined
        );
        if (!cancelled) setSummary(result);
      } catch {
        if (!cancelled) setSummary(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    // Snapshots (the trend timeline) are an admin-only, account-trend endpoint
    // — not exposed to a group-owner learner, so skip the fetch in owner mode.
    if (ownerMode || !effectiveWorkspaceId) { setSnapshots([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const result = await reportsClient.complianceSnapshots(effectiveWorkspaceId, snapshotDays, undefined);
        if (!cancelled) setSnapshots(result);
      } catch {
        if (!cancelled) setSnapshots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveWorkspaceId, snapshotDays, ownerMode]);

  useEffect(() => {
    if (!effectiveWorkspaceId || viewMode !== 'heatmap') return;
    let cancelled = false;
    setHeatmapLoading(true);
    (async () => {
      try {
        const cells = await reportsClient.complianceHeatmap(
          pivotBy,
          effectiveWorkspaceId,
          userIds.length > 0 ? userIds : undefined,
          courseIdArr.length > 0 ? courseIdArr : undefined,
          groupIdArr.length > 0 ? groupIdArr : undefined,
          includeUngrouped,
          tagIdArr.length > 0 ? tagIdArr : undefined,
          undefined,
          statusValue,
          undefined, undefined,
          dueFromIso as any, dueToIso as any,
          undefined
        );
        if (!cancelled) setHeatmapCells(cells);
      } catch {
        if (!cancelled) {
          setHeatmapCells([]);
          alert(enqueueSnackbar, 'Failed to load heatmap', 'error');
        }
      } finally {
        if (!cancelled) setHeatmapLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, pivotBy, viewMode]);

  const paged = usePagedList<CourseEnrollment>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        if (!effectiveWorkspaceId) {
          return { items: [], totalCount: 0, page, pageSize };
        }
        const result = await reportsClient.complianceSearch(
          effectiveWorkspaceId,
          userIds.length > 0 ? userIds : undefined,
          courseIdArr.length > 0 ? courseIdArr : undefined,
          groupIdArr.length > 0 ? groupIdArr : undefined,
          includeUngrouped,
          tagIdArr.length > 0 ? tagIdArr : undefined,
          undefined,
          statusValue,
          undefined, undefined,
          dueFromIso as any, dueToIso as any,
          search || undefined,
          undefined, // sortKey
          false,     // sortDescending
          page,
          pageSize
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filterKey]
    ),
    deps: [filterKey],
    initialSearch: initialQuery,
    onSearchChange: (s) => setParams({ q: s || null }),
    onError: () => alert(enqueueSnackbar, 'Failed to load compliance data', 'error'),
  });

  // Heatmap shape derived from cells
  const { rowKeys, colKeys, matrix } = useMemo(() => {
    const matrix: Record<string, Record<string, ComplianceHeatmapCell>> = {};
    for (const c of heatmapCells) {
      if (!matrix[c.rowKey]) matrix[c.rowKey] = {};
      matrix[c.rowKey][c.colKey] = c;
    }
    const rowKeys = Object.keys(matrix).sort();
    const colKeys = Array.from(new Set(rowKeys.flatMap(r => Object.keys(matrix[r])))).sort();
    return { rowKeys, colKeys, matrix };
  }, [heatmapCells]);

  // ─── Drill-down ────────────────────────────────────────────────────
  const applyDrillStep = (
    addUserId?: number,
    addCourseId?: number,
    addGroupId?: number | null,  // null/0 = "No Group" sentinel
    addTagId?: number | null,     // null/0 = "Untagged" sentinel
    nextPivot?: PivotMode,
    nextViewMode?: ViewMode
  ) => {
    const added = {
      userIds: addUserId != null && !userIds.includes(addUserId) ? [addUserId] : [],
      courseIds: addCourseId != null && !courseIdArr.includes(addCourseId) ? [addCourseId] : [],
      groupIds: addGroupId != null && addGroupId !== 0 && !groupIdArr.includes(addGroupId) ? [addGroupId] : [],
      includeUngrouped: (addGroupId === null || addGroupId === 0) && !includeUngrouped,
      tagIds: addTagId != null && addTagId !== 0 && !tagIdArr.includes(addTagId) ? [addTagId] : [],
    };

    setDrillStack(prev => [...prev, { pivotBefore: pivotBy, viewModeBefore: viewMode, added }]);

    const patch: Record<string, string | number | number[] | boolean | null> = {};
    if (added.userIds.length > 0)   patch.userIds = [...userIds, ...added.userIds];
    if (added.courseIds.length > 0) patch.courseIds = [...courseIdArr, ...added.courseIds];
    if (added.groupIds.length > 0)  patch.groupIds = [...groupIdArr, ...added.groupIds];
    if (added.includeUngrouped)     patch.includeUngrouped = true;
    if (added.tagIds.length > 0)    patch.tagIds = [...tagIdArr, ...added.tagIds];
    if (nextPivot)    patch.pivot = nextPivot;
    if (nextViewMode) patch.view = nextViewMode;
    setParams(patch);
  };

  const onClickRowHeader = (cell: ComplianceHeatmapCell) => {
    if (pivotBy === 'group') {
      applyDrillStep(undefined, undefined, cell.rowId != null ? Number(cell.rowId) : null, undefined, 'person');
    } else if (pivotBy === 'person') {
      if (cell.rowId != null) applyDrillStep(Number(cell.rowId), undefined, undefined, undefined, 'course');
    } else if (pivotBy === 'course') {
      if (cell.rowId != null) applyDrillStep(undefined, Number(cell.rowId), undefined, undefined, 'person');
    } else if (pivotBy === 'tag') {
      if (cell.rowId != null) applyDrillStep(undefined, undefined, undefined, Number(cell.rowId), 'course');
    }
  };

  const onClickCell = (cell: ComplianceHeatmapCell) => {
    if (pivotBy === 'group') {
      applyDrillStep(undefined, cell.colId != null ? Number(cell.colId) : undefined, cell.rowId != null ? Number(cell.rowId) : null, undefined, undefined, 'table');
    } else if (pivotBy === 'person') {
      applyDrillStep(cell.rowId != null ? Number(cell.rowId) : undefined, cell.colId != null ? Number(cell.colId) : undefined, undefined, undefined, undefined, 'table');
    } else if (pivotBy === 'course') {
      applyDrillStep(undefined, cell.rowId != null ? Number(cell.rowId) : undefined, cell.colId != null ? Number(cell.colId) : null, undefined, undefined, 'table');
    } else if (pivotBy === 'tag') {
      applyDrillStep(undefined, undefined, cell.colId != null ? Number(cell.colId) : null, cell.rowId != null ? Number(cell.rowId) : null, undefined, 'table');
    }
  };

  const navigateBack = () => {
    if (drillStack.length === 0) return;
    const last = drillStack[drillStack.length - 1];
    setDrillStack(prev => prev.slice(0, -1));

    const removeUsers = new Set(last.added.userIds);
    const removeCourses = new Set(last.added.courseIds);
    const removeGroups = new Set(last.added.groupIds);
    const removeTags = new Set(last.added.tagIds);

    setParams({
      pivot: last.pivotBefore,
      view: last.viewModeBefore,
      userIds: userIds.filter(id => !removeUsers.has(id)),
      courseIds: courseIdArr.filter(id => !removeCourses.has(id)),
      groupIds: groupIdArr.filter(id => !removeGroups.has(id)),
      includeUngrouped: last.added.includeUngrouped ? false : includeUngrouped,
      tagIds: tagIdArr.filter(id => !removeTags.has(id)),
    });
  };

  const hasAnyFilter =
    filterStatus !== undefined || filterCourseTagIds.length > 0 ||
    filterPeople.length > 0 || filterCourses.length > 0 || filterGroupSelections.length > 0 ||
    !!filterDueFrom || !!filterDueTo;

  const clearAllFilters = () => {
    setDrillStack([]);
    setParams({
      status: null, tagIds: null, userIds: null, courseIds: null,
      groupIds: null, includeUngrouped: false, dueFrom: null, dueTo: null,
    });
  };

  const groupSelectOptions = useMemo<(Group | { id: 0; name: 'No Group' })[]>(
    () => [...groups, { id: 0 as const, name: 'No Group' as const }],
    [groups]
  );

  const setGroupSelections = (next: (Group | { id: 0; name: 'No Group' })[]) => {
    const realGroupIds = next.filter(g => g.id !== 0).map(g => g.id!);
    const wantUngrouped = next.some(g => g.id === 0);
    setParams({ groupIds: realGroupIds, includeUngrouped: wantUngrouped });
  };

  const handleDownloadCsv = async () => {
    if (!effectiveWorkspaceId) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set('workspaceId', String(effectiveWorkspaceId));
      for (const id of userIds) params.append('userIds', String(id));
      for (const id of courseIdArr) params.append('courseIds', String(id));
      for (const id of groupIdArr) params.append('groupIds', String(id));
      if (includeUngrouped) params.set('includeUngrouped', 'true');
      for (const id of tagIdArr) params.append('courseTagIds', String(id));
      if (statusValue !== undefined) params.set('assignmentStatus', String(statusValue));
      if (dueFromIso) params.set('dueFrom', dueFromIso);
      if (dueToIso) params.set('dueTo', dueToIso);

      const base = process.env.REACT_APP_API_BASE_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${base}/api/Reports/compliance/export/csv?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error('CSV download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert(enqueueSnackbar, 'Failed to download CSV', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const pivotRowLabel = (mode: PivotMode) => {
    switch (mode) {
      case 'group':  return 'Group';
      case 'person': return 'Person';
      case 'course': return 'Course';
      case 'tag':    return 'Tag';
    }
  };

  const ellipsisSx = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as const;

  if (!effectiveWorkspaceId) {
    return (
      <EmptyState
        icon={<WorkspacesIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="Select a workspace to view this report"
        subtitle="Use the workspace switcher in the sidebar to choose a workspace"
      />
    );
  }

  // Capture current URL so entity-link state restores filters on Back.
  const fromUrl = `${location.pathname}${location.search}`;
  const entityState = { from: fromUrl, fromText: 'Back to Compliance Dashboard' };

  const tableColumns: PagedTableColumn<CourseEnrollment>[] = [
    {
      key: 'name',
      label: 'Student',
      render: (e) => {
        const name = (
          <>
            <Typography variant="body2" fontWeight={500}>{e.lastName}, {e.firstName}</Typography>
            <Typography variant="caption" color="text.secondary">{e.email}</Typography>
          </>
        );
        // Owner mode (group-admin learner): the student profile page is an
        // admin surface, so render the name as plain text rather than a link.
        return ownerMode ? (
          <Box>{name}</Box>
        ) : (
          <EntityLink
            to={e.userId != null ? `/students/${e.userId}` : null}
            tooltip="Open student profile"
            state={entityState}
          >
            {name}
          </EntityLink>
        );
      },
    },
    {
      key: 'course',
      label: 'Course',
      render: (e) => (
        <EntityLink
          to={e.courseId != null ? `/course/${e.courseId}` : null}
          tooltip="Open course"
          state={entityState}
        >
          <span>{e.courseTitle}</span>
        </EntityLink>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (e) => (
        <Chip
          label={statusLabel(e.assignmentStatus ?? null)}
          size="small"
          sx={{ bgcolor: statusColor(e.assignmentStatus ?? null) }}
        />
      ),
    },
    { key: 'deadline', label: 'Due Date', render: (e) => e.deadline ? new Date(e.deadline).toLocaleDateString() : '—' },
    { key: 'completed', label: 'Completed', render: (e) => e.completedAt ? new Date(e.completedAt).toLocaleDateString() : '—' },
    { key: 'progress', label: 'Progress', align: 'right', render: (e) => `${Math.round(e.progressPercentage ?? 0)}%` },
  ];

  return (
    <Box>
      <ComplianceSummaryStrip
        summary={summary}
        snapshots={snapshots}
        days={snapshotDays}
        onChangeDays={(days) => setParams({ snapDays: days })}
      />

      {drillStack.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" color="primary" startIcon={<ArrowBackIcon />} onClick={navigateBack}>
            Back
          </Button>
        </Stack>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setParams({ view: v })}
          size="small"
        >
          <ToggleButton value="table">Table</ToggleButton>
          <ToggleButton value="heatmap">Heatmap</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined" color="primary" size="small"
          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadCsv} disabled={downloading}
        >
          {downloading ? 'Preparing…' : 'CSV'}
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3, alignItems: 'center', rowGap: 2 }}>
        {viewMode === 'heatmap' && (
          <ToggleButtonGroup
            value={pivotBy}
            exclusive
            onChange={(_, v) => v && setParams({ pivot: v })}
            size="small"
          >
            <ToggleButton value="group">By Group</ToggleButton>
            <ToggleButton value="person">By Person</ToggleButton>
            <ToggleButton value="course">By Course</ToggleButton>
            <ToggleButton value="tag">By Tag</ToggleButton>
          </ToggleButtonGroup>
        )}

        {!ownerMode && (
          <UserAutocomplete
            multiple
            value={filterPeople}
            onChange={(v) => {
              const next = (v as Login[]) ?? [];
              setFilterPeople(next);
              setParams({ userIds: next.map(p => p.id!).filter(Boolean) });
            }}
            workspaceId={effectiveWorkspaceId}
            label="People"
            sx={{ minWidth: 220 }}
          />
        )}

        <CourseAutocomplete
          multiple
          value={filterCourses}
          onChange={(v) => {
            const next = (v as Course[]) ?? [];
            setFilterCourses(next);
            setParams({ courseIds: next.map(c => c.id!).filter(Boolean) });
          }}
          label="Courses"
          sx={{ minWidth: 200 }}
        />

        <Autocomplete
          multiple
          options={groupSelectOptions}
          getOptionLabel={g => g.name ?? ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={filterGroupSelections}
          onChange={(_, v) => setGroupSelections(v)}
          size="small"
          sx={{ minWidth: 180 }}
          renderInput={params => <TextField {...params} label="Groups" />}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.name} size="small" {...getTagProps({ index })} />
            ))
          }
        />

        {!ownerMode && (
          <TagAutocomplete
            multiple
            value={filterCourseTagIds}
            onChange={(v) => {
              const next = (v as Tag[]) ?? [];
              setFilterCourseTagIds(next);
              setParams({ tagIds: next.map(t => t.id!).filter(Boolean) });
            }}
            workspaceId={effectiveWorkspaceId ?? undefined}
            label="Tags"
            sx={{ minWidth: 180 }}
          />
        )}

        <Select
          value={filterStatus ?? ''}
          onChange={e => {
            // 0 is never a valid assignment-status value, so falsy = "all".
            const raw = e.target.value as number | '';
            setParams({ status: raw ? Number(raw) : null });
          }}
          displayEmpty size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value={AssignmentStatus.Upcoming}>Upcoming</MenuItem>
          <MenuItem value={AssignmentStatus.Active}>In Progress</MenuItem>
          <MenuItem value={AssignmentStatus.Overdue}>Overdue</MenuItem>
          <MenuItem value={AssignmentStatus.Completed}>Completed</MenuItem>
        </Select>

        <TextField label="From" type="date" size="small" value={filterDueFrom}
                   onChange={e => setParams({ dueFrom: e.target.value || null })} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
        <TextField label="To" type="date" size="small" value={filterDueTo}
                   onChange={e => setParams({ dueTo: e.target.value || null })} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />

        {hasAnyFilter && (
          <Button size="small" variant="text" color="inherit" onClick={clearAllFilters}>
            Clear filters
          </Button>
        )}
      </Stack>

      {viewMode === 'heatmap' && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {[AssignmentStatus.Overdue, AssignmentStatus.Active, AssignmentStatus.Upcoming, AssignmentStatus.Completed].map(s => (
            <Chip key={s} label={statusLabel(s)} size="small" sx={{ bgcolor: statusColor(s) }} />
          ))}
          <Chip label="No assignment" size="small" sx={{ bgcolor: 'grey.100' }} />
        </Stack>
      )}

      {viewMode === 'heatmap' ? (
        heatmapLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : rowKeys.length === 0 ? (
          <EmptyState
            icon={<FilterIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
            title="No results match the current filters"
            subtitle="Try adjusting the filters above"
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 200, bgcolor: 'grey.50' }}>
                    {pivotRowLabel(pivotBy)}
                  </TableCell>
                  {colKeys.map(col => (
                    <TableCell key={col} sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 140, maxWidth: 200 }}>
                      <Tooltip title={col}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, ...ellipsisSx }}>
                          {col}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rowKeys.map(row => {
                  const firstCellInRow = colKeys.map(c => matrix[row]?.[c]).find(c => !!c);
                  return (
                    <TableRow key={row} hover>
                      <TableCell
                        sx={{
                          fontWeight: 600, minWidth: 200, maxWidth: 260,
                          cursor: 'pointer', color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => firstCellInRow && onClickRowHeader(firstCellInRow)}
                      >
                        <Tooltip title={`Filter to ${row}`}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, ...ellipsisSx }}>{row}</Typography>
                        </Tooltip>
                      </TableCell>
                      {colKeys.map(col => {
                        const cell = matrix[row]?.[col];
                        const worst = cell ? cellWorstStatus(cell) : null;
                        return (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: statusColor(worst),
                              cursor: cell ? 'pointer' : 'default',
                              textAlign: 'center',
                              '&:hover': cell ? { opacity: 0.8 } : {},
                            }}
                            onClick={() => cell && onClickCell(cell)}
                          >
                            {cell ? (
                              <Tooltip title={`${cell.completed}/${cellTotal(cell)} completed · click to see records`}>
                                <Typography variant="caption">{cellPercent(cell)}</Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <PagedTable<CourseEnrollment>
          columns={tableColumns}
          rows={paged.items}
          totalCount={paged.totalCount}
          page={paged.page}
          pageSize={paged.pageSize}
          loading={paged.loading}
          rowKey={(r) => `${r.userId}-${r.courseId}-${r.assignmentDefinitionId ?? 'none'}`}
          onPageChange={paged.setPage}
          onPageSizeChange={paged.setPageSize}
          search={paged.search}
          searchPlaceholder="Search name, email, course…"
          onSearchChange={paged.setSearch}
          emptyMessage="No results match the current filters"
        />
      )}
    </Box>
  );
}
